import { Injectable, inject, signal, computed } from '@angular/core';
import { SsoCryptoService } from './sso-crypto.service';

/** User-facing theme choice including OS follow mode. */
export type ThemePreference = 'light' | 'dark' | 'system';

/** Resolved theme applied to the document. */
export type ResolvedTheme = 'light' | 'dark';

/** @deprecated Use ThemePreference or ResolvedTheme. */
export type Theme = ResolvedTheme;

const STORAGE_KEY = 'pathways-theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  // PAT-385 — declared first so inject() is available when preference signal initialises.
  private readonly crypto = inject(SsoCryptoService);

  private mediaQuery: MediaQueryList | null = null;
  private readonly onSystemThemeChange = (): void => {
    if (this.preference() === 'system') {
      this.applyToDocument();
    }
  };

  private readonly preference = signal<ThemePreference>(this.readStoredPreference());

  /** Stored user preference (light, dark, or system). */
  themePreference = computed(() => this.preference());

  /** Resolved light/dark theme currently applied. */
  resolvedTheme = computed(() => this.resolveTheme(this.preference()));

  /** Alias for templates that read the active theme. */
  theme = computed(() => this.resolvedTheme());

  isDark = computed(() => this.resolvedTheme() === 'dark');

  constructor() {
    if (typeof document !== 'undefined') {
      this.bindSystemThemeListener();
      this.applyToDocument();
    }
  }

  setThemePreference(value: ThemePreference): void {
    this.preference.set(value);
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, value);
      } catch { /* ignore quota / private-mode errors */ }
    }
    this.applyToDocument();
  }

  /** Sets an explicit light or dark preference (not system). */
  setTheme(value: ResolvedTheme): void {
    this.setThemePreference(value);
  }

  /** Quick toggle between light and dark (does not select system). */
  toggleTheme(): void {
    this.setThemePreference(this.resolvedTheme() === 'light' ? 'dark' : 'light');
  }

  applyToDocument(value?: ThemePreference): void {
    const pref = value ?? this.preference();
    const resolved = this.resolveTheme(pref);
    const doc = typeof document !== 'undefined' ? document : null;
    if (!doc) return;

    doc.body.classList.remove('theme-light', 'theme-dark');
    doc.body.classList.add(`theme-${resolved}`);
    doc.body.setAttribute('data-theme', resolved);
    doc.documentElement.setAttribute('data-theme', resolved);
    doc.documentElement.setAttribute('data-theme-preference', pref);

    const meta = doc.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    if (meta) {
      meta.content = resolved === 'dark' ? '#171717' : '#ffffff';
    }
  }

  private readStoredPreference(): ThemePreference {
    if (typeof localStorage === 'undefined') return 'light';
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 'light';
    let v: string;
    try {
      v = this.crypto.decryptAES(raw).trim();
      if (v === 'light' || v === 'dark' || v === 'system') {
        try { localStorage.setItem(STORAGE_KEY, v); } catch { /* ignore */ }
      }
    } catch {
      v = raw;
    }
    if (v === 'light' || v === 'dark' || v === 'system') return v;
    return 'light';
  }

  private resolveTheme(preference: ThemePreference): ResolvedTheme {
    if (preference === 'light' || preference === 'dark') return preference;
    return this.systemPrefersDark() ? 'dark' : 'light';
  }

  private systemPrefersDark(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  private bindSystemThemeListener(): void {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (typeof this.mediaQuery.addEventListener === 'function') {
      this.mediaQuery.addEventListener('change', this.onSystemThemeChange);
    } else {
      this.mediaQuery.addListener(this.onSystemThemeChange);
    }
  }
}
